import { useRef, useState, useEffect, useMemo } from 'react';

// utils
import { useOutsideClick } from 'utils/hooks/use-outside-click';
import { isEmpty } from 'utils/is-empty';
import { useElementWidth } from 'utils/hooks/use-element-width';

export function useBreadcrumbs(items: Partial<Breadcrumb>[] = []) {
  const olRef = useRef<HTMLElement>(null);
  const hiddenNavRef = useRef<HTMLElement>(null);
  const hiddenOlRef = useRef<HTMLOListElement>(null);
  const [dotsPosition, setDotsPosition] = useState<'none' | 'start' | 'end'>('none');
  const navElementWidth = useElementWidth(hiddenNavRef, 100);
  const olElementWidth = useElementWidth(hiddenOlRef, 100);

  useOutsideClick(olRef, closePopover);

  useEffect(() => {
    if (navElementWidth && olElementWidth) {
      const isOverflow = olElementWidth > navElementWidth;
      if (isOverflow) {
        if (dotsPosition === 'none') {
          setDotsPosition('start');
        }
      }
    }
  }, [navElementWidth, olElementWidth, dotsPosition]);

  useEffect(() => {
    if (dotsPosition !== 'none' && document.activeElement) {
      olRef.current?.querySelector<HTMLButtonElement>("[data-dots='true']")?.focus();
    }
  }, [dotsPosition]);

  const { elementsToTruncate } = useMemo(() => {
    if (!olElementWidth || !navElementWidth || olElementWidth < navElementWidth) {
      return { elementsToTruncate: 0 };
    }

    const lis = Array.from(hiddenOlRef.current?.querySelectorAll('li') ?? []);
    let offset = -50;
    let elementsToTruncate = 0;

    if (dotsPosition === 'end') {
      lis.reverse();
    }

    lis.forEach((li, index) => {
      const liWidth = li.getBoundingClientRect().width;

      if (olElementWidth - offset > navElementWidth && index > 0) {
        elementsToTruncate++;
        offset += liWidth;
      }
    });

    return { elementsToTruncate };
  }, [dotsPosition, navElementWidth, olElementWidth]);

  function getItems(itemsToSkip: number) {
    let results: Partial<Breadcrumb>[] = [];
    if (itemsToSkip === 0 || dotsPosition === 'none') {
      return items;
    }

    if (dotsPosition === 'start') {
      results = [
        items[0],
        {
          dots: true,
          onClick: () => {
            setDotsPosition('end');
          },
        },
        ...items.slice(1 + itemsToSkip),
      ] as Partial<Breadcrumb>[];
    } else if (dotsPosition === 'end') {
      results = [
        ...items.slice(0, -(itemsToSkip + 1)),
        {
          dots: true,
          onClick: () => {
            setDotsPosition('start');
          },
        },
        items[items.length - 1],
      ] as Partial<Breadcrumb>[];
    }

    return results;
  }

  function findElementIndexInNodeList(all: NodeListOf<HTMLElement>, element: HTMLElement): number {
    return Array.from(all).indexOf(element);
  }

  function handleOnKeyDown(event: KeyboardEvent) {
    const all = document.querySelectorAll<HTMLAnchorElement | HTMLButtonElement>(
      '[data-focusable-breadcrumb-item="true"]',
    );
    const currentEl = document.activeElement
      ?.closest('li')
      ?.querySelector('[data-focusable-breadcrumb-item="true"]') as HTMLElement;
    const currentIndex = findElementIndexInNodeList(all, currentEl);

    const keyMap: { [key: string]: () => void } = {
      ArrowRight: () => {
        const nextElement = all[currentIndex + 1] as HTMLElement;
        closePopover();

        if (nextElement) {
          nextElement.focus();
        }
      },
      ArrowLeft: () => {
        const prevElement = all[currentIndex - 1] as HTMLElement;
        closePopover();

        if (prevElement) {
          prevElement.focus();
        }
      },
      ArrowDown: () => {
        const target = event.target as HTMLElement | null;
        if (target && target.tagName === 'BUTTON') {
          target.classList.add('opened');
          target.parentElement?.querySelector<HTMLElement>('div> a:not([aria-disabled])')?.focus();
        } else {
          const currentLink = document.activeElement as HTMLAnchorElement;
          const nextElement = currentLink.nextElementSibling as HTMLAnchorElement;
          if (nextElement) {
            nextElement.focus();
          }
        }
      },
      ArrowUp: () => {
        const currentLink = document.activeElement as HTMLAnchorElement;
        const prevElement = currentLink.previousElementSibling as HTMLAnchorElement;
        if (prevElement && prevElement.tabIndex >= 0) {
          prevElement.focus();
        }
      },
      Escape: () => {
        closePopover();
      },
    };

    const handler = keyMap[event.key];

    if (handler) {
      handler();
      event.preventDefault();
    }
  }

  function closePopover(expectOf?: HTMLElement) {
    olRef.current?.querySelectorAll('.opened').forEach((el) => {
      if (expectOf && el === expectOf) return;
      el.classList.remove('opened');
    });
  }

  return {
    hiddenNavProps: {
      ref: hiddenNavRef,
      'aria-hidden': true,
      style: { visibility: 'hidden', height: 0, overflow: 'hidden' },
    },
    hiddenOlProps: { ref: hiddenOlRef },
    navProps: {
      'aria-label': 'Breadcrumb',
    },
    olProps: {
      ref: olRef,
      onKeyDown: (e) => handleOnKeyDown(e),
      onClick: (e) => {
        const button = e.target.closest('button') as HTMLButtonElement;
        if (!button) return;

        closePopover(button);
        button.classList.toggle('opened');
      },
    },
    truncatedBreadcrumbs: getItems(elementsToTruncate).map((ele, index) => {
      const clone = { ...ele };

      const isDisabled =
        !clone.dots &&
        (isEmpty(clone.url) ||
          clone.isDisabled ||
          !clone?.siblings ||
          (clone.siblings?.length <= 1 && index > 0)) === true;
      if (ele) {
        clone['data-focusable-breadcrumb-item'] = !isDisabled;
        if (isDisabled) {
          clone['aria-disabled'] = true;
          clone['tabIndex'] = -1;
        }
        if (ele.dots) {
          clone['data-dots'] = true;
        }
      }

      return clone;
    }),
  };
}
